import { decodeJSOG } from 'utils/jsonUtils';
import { getServerBaseUrl } from 'utils/urlUtils';
import { api } from './api';

// Define a service using a base URL and expected endpoints
export const entityApi = api.injectEndpoints({
  reducerPath: 'entityApi',
  tagTypes: ['Entities', 'Contents', 'RootFolders'],
  endpoints: (builder) => ({
    findByTerm: builder.query({
      query: ({ term }) => ({
        url: `/arcade/api/entities/search?term=${term}&entityTypeCodes=DELIB`,
        method: 'GET'
      }),
      providesTags: (result) =>
        result?.content
          ? [...result.content.map(({ id }) => ({ type: 'Entities', id }))]
          : ['Entities']
    }),
    findByClauses: builder.query({
      query: ({ viewName, clauses, entityTypeCodes, page, size, sort }) => ({
        url: `/arcade/api/entities/search`,
        method: 'POST',
        params: { viewName, entityTypeCodes, page, size, sort },
        body: clauses
      }),
      providesTags: (result) =>
        result?.content
          ? [...result.content.map(({ id }) => ({ type: 'Entities', id }))]
          : ['Entities']
    }),
    findBySpecification: builder.query({
      query: ({
        viewName,
        filter,
        page = 0,
        size = 20,
        sort = 'id,desc',
        projection,
        jsonView,
        baseType
      }) => ({
        url: `public/api/entities/search/findBySpecification`,
        method: 'GET',
        params: { viewName, filter, page, size, sort, projection, jsonView, baseType }
      }),
      transformResponse: (response, meta, { jsonView, depth }) => {
        response.decode = () => {
          if (jsonView?.includes('WithParent')) {
            return decodeJSOG(response, depth);
          }
          return response;
        };
        return response;
      },
      providesTags: (result) =>
        result?.content
          ? [...result.content.map(({ id }) => ({ type: 'Entities', id }))]
          : ['Entities']
    }),
    getContent: builder.query({
      query: ({ contentId, mimeType }) => ({
        url: `arcade/api/entities/${contentId}/content`,
        method: 'GET',
        headers: {
          'content-type': mimeType
        },
        responseHandler: (response) => response.blob()
      }),
      providesTags: (result, error, arg) => [{ type: 'Contents', contentId: arg.contentId }]
    }),
    getPdfFile: builder.query({
      queryFn: async ({ contentId }) => {
        const url = `${getServerBaseUrl()}/arcade/api/entities/${contentId}/content`;
        const response = await fetch(url, {
          headers: {
            'content-type': 'application/pdf'
          }
        });
        const blob = await response.blob();
        return { data: blob };
      },
      providesTags: (result, error, arg) => [{ type: 'Contents', contentId: arg.contentId }]
    }),
    getRelatedDocuments: builder.query({
      query: ({ entityId, entityTypeCodes, resultMax = 10, scoreMin = 20, withTag = false }) => ({
        url: `arcade/api/entities/search/${entityId}/moreLikeThis`,
        method: 'GET',
        params: { entityTypeCodes, resultMax, scoreMin, withTag }
      }),
      transformResponse: (response) => {
        if (response && response.content) {
          return {
            ...response,
            content: [...response.content].sort((a, b) => b.score - a.score) // sort by score
          };
        }
        return response;
      },
      providesTags: (result) =>
        result?.content
          ? [...result.content.map(({ entity }) => ({ type: 'RelatedDocuments', id: entity.id }))]
          : ['RelatedDocuments']
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useFindByTermQuery,
  useFindByClausesQuery,
  useFindBySpecificationQuery,
  useLazyGetContentQuery,
  useGetPdfFileQuery,
  useGetRelatedDocumentsQuery
} = entityApi;
